import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnInit, output, signal, untracked, viewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { QuillEditorComponent } from 'ngx-quill';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReplyTemplateService, TagListItem } from '../../../reply-template.service';
import Delta from 'quill-delta';
import { FeedbackService } from '../../../../../core/feedback.service';
import { NgClass } from '@angular/common';
import { ClickOutsideModule } from 'ng-click-outside';
import { AiSettings, AiSettingsCreatePayload, AiSettingsType } from '../ai-replies-settings.component';
import { MatTooltip } from '@angular/material/tooltip';
import { EmojiPickerComponent } from '../../../../global/emoji-picker/emoji-picker.component';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { GxInputDirective } from '../../../../../directives/gxinput.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-edit-greetings',
  standalone: true,
  imports: [
    FormsModule,
    EmojiPickerComponent,
    MatButton,
    MatFormField,
    MatHint,
    MatIcon,
    MatIconButton,
    MatInput,
    MatMenu,
    MatProgressSpinner,
    QuillEditorComponent,
    ReactiveFormsModule,
    TranslateModule,
    MatMenuTrigger,
    NgClass,
    ClickOutsideModule,
    MatTooltip,
    MatChip,
    MatChipSet,
    GxInputDirective,
  ],
  templateUrl: './edit-greetings.component.html',
  styleUrl: './edit-greetings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditGreetingsComponent implements OnInit {
  data = input<AiSettings | null>();
  settingType = input<AiSettingsType>();

  editedSetting = output<any>();
  deletedSetting = output<any>();
  editingOpen = output<any>();

  quillEditor = viewChild<QuillEditorComponent>('quillEditor');

  private readonly translate = inject(TranslateService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly replyTemplateService = inject(ReplyTemplateService);
  private readonly destroyRef = inject(DestroyRef);

  formGroup: FormGroup<{
    templateName: FormControl<string>,
    templateText: FormControl<string>,
    templateObject: FormControl<Delta | null>
  }>;

  readonly nameMaxLength: number = 40;
  readonly textMaxLength: number = 128;

  protected readonly wasTemplateNameTooLong = signal(false);
  protected readonly savingInProgress = signal(false);
  protected readonly deletionInProgress = signal(false);
  protected readonly infoMenuOpened = signal(false);

  tagList = this.replyTemplateService.generateTagList();
  // https://github.com/afry/quill-mention
  quillModules = {
    toolbar: false,
    mention: {
      spaceAfterInsert: false,
      // Allowed characters in search term triggering a search request using regular expressions.
      allowedChars: /^[A-Za-z\s]*$/,
      mentionDenotationChars: ['@'],
      showDenotationChar: false,
      // mentions menu will cause horizontal overflow if it does not have enough space, so render it
      // below the editor
      fixMentionsToQuill: true,
      source: (searchTerm, renderList) => {
        if (searchTerm.length === 0) {
          renderList(this.tagList, searchTerm);
        } else {
          const matches = [];

          this.tagList.forEach((tag) => {
            if (tag.value.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1) {
              matches.push(tag);
            }
          });
          renderList(matches, searchTerm);
        }
      },
    },
  };

  constructor() {
    // Synchronize form fields reactively on incoming data Signal change
    effect(() => {
      const item = this.data();
      untracked(() => {
        if (this.formGroup) {
          this.formGroup.get('templateName').setValue(item?.title ? item.title : '');
          this.formGroup.get('templateText').setValue(item?.text ? item.text : '');
          this.formGroup.get('templateObject').setValue(item?.text ? this.replyTemplateService.generateQuillDelta(item.text) : null);
        }
      });
    });
  }

  ngOnInit() {
    const item = this.data();
    this.formGroup = new FormGroup({
      templateName: new FormControl(item?.title ? item.title : '',
          {
            nonNullable: true,
            validators: [this.fixNameLengthIfExceeded.bind(this)],
          }),
      templateText: new FormControl(item?.text ? item.text : '',
          { nonNullable: true, validators: [Validators.maxLength(this.textMaxLength)] }),
      templateObject: new FormControl(item?.text ? this.replyTemplateService.generateQuillDelta(item.text) : null),
    });

    this.formGroup.get('templateObject').valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
    ).subscribe((val => {
      this.formGroup.get('templateText').setValue(this.replyTemplateService.generateReplyTextFromDelta(val));
    }));
  }

  /**
   * Checks if a new greeting/closing is to be created (true) or if an existing template gets edited (false).
   */
  isNew(): boolean {
    return this.data()?.id == null;
  }

  /**
   * If the user typed in a template name that's too long, it will get cut off and wasTemplateNameTooLong will be set
   * to true.
   * @param control
   */
  fixNameLengthIfExceeded(control: AbstractControl) {
    if (control.value.length > this.nameMaxLength) {
      control.setValue(control.value.substr(0, this.nameMaxLength));
      this.wasTemplateNameTooLong.set(true);
      return null;
    }
    this.wasTemplateNameTooLong.set(false);
    return null;
  }

  /**
   * Checks if the user should be able to click the save-button.
   */
  canSubmit() {
    if (!this.formGroup.controls.templateName.value || !this.formGroup.controls.templateText.value) {
      return false;
    }
    let templateName = this.formGroup.controls.templateName.value;
    let templateText = this.formGroup.controls.templateText.value;

    return this.formGroup.valid && templateName.trim().length > 0 && templateText.trim().length > 0
        && (templateName != this.data()?.title || templateText != this.data()?.text);
  }

  /**
   * Saves the edited or newly created greeting/closing and updates it in the template list.
   */
  onSave() {
    if (this.formGroup.invalid || this.savingInProgress()) {
      return;
    }

    this.savingInProgress.set(true);

    if (this.isNew()) {
      let emittedData: AiSettingsCreatePayload = {
        title: this.formGroup.controls.templateName.value,
        text: this.formGroup.controls.templateText.value,
        type: this.settingType(),
        selected: this.data()?.selected,
      };
      this.editedSetting.emit(emittedData);
    } else {
      let emittedData = {
        id: this.data()?.id,
        selected: this.data()?.selected,
        title: this.formGroup.controls.templateName.value,
        text: this.formGroup.controls.templateText.value,
      };
      this.editedSetting.emit(emittedData);
    }
  }

  /**
   * If the user cancels the greeting/closing-editing, the popup gets closed.
   */
  onCancel() {
    this.onBack();
  }

  /**
   * Deletes the greeting/closing
   */
  onDelete() {
    const item = this.data();
    this.feedbackService.showConfirmationPopup({
      data: {
        title: this.translate.instant('REVIEWS.TEMPLATES.DELETE_GREETING_CLOSING?', {
          title: item.title,
          type: item.type.toLowerCase(),
        }),
        text: this.translate.instant('REVIEWS.TEMPLATES.DELETE_GREETING_CLOSING_INFO', { type: item.type.toLowerCase() }),
        submitButtonText: this.translate.instant('DELETE'),
        cancelButtonText: this.translate.instant('CANCEL'),
      },
      disableClose: true,
    }).pipe(
        takeUntilDestroyed(this.destroyRef),
    ).subscribe((shouldDelete: boolean) => {
      if (shouldDelete) {
        this.deletionInProgress.set(true);
        this.deletedSetting.emit(item?.id);
      }
    });
  }

  /**
   * Programmatically inserts a name tag at the current text cursor's position
   */
  insertNameTag(tagListItem: TagListItem): void {
    const editor = this.quillEditor();
    editor?.quillEditor?.getModule('mention')?.['insertItem'](
        { id: tagListItem.id, value: tagListItem.value }, true,
    );
  }

  /**
   * Event handler for opening info menu
   */
  onInfoMenuOpened(): void {
    this.infoMenuOpened.set(true);
  }

  /**
   * Event handler for closing info menu
   */
  onInfoMenuClosed(): void {
    this.infoMenuOpened.set(false);
  }

  onBack() {
    this.editingOpen.emit(false);
  }

  /**
   * Insert emoji into Quill editor programmatically
   * @param emoji
   * @param quillEditorRef
   */
  insertEmoji(emoji: string, quillEditorRef: QuillEditorComponent) {
    this.replyTemplateService.insertEmoji(emoji, quillEditorRef);
  }

  protected readonly AiSettingsType = AiSettingsType;
}
